import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Input, Spin} from 'antd';
import {debounce} from 'lodash';
import {getSearchLocation} from "../services/api";
import {ILocation} from "../services/types";
import {ModalComponent} from "../pages/candidatePool/ModalComponent.tsx";
import {defaultWhereOptions} from "../util/utils.ts";
import styled from "styled-components";
import {WhatOption} from "./ProfessionModal.tsx";
import {colors} from "../util/colors.ts";

interface IAddressModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (value: ILocation | null) => void;
    initialValue?: ILocation | null;
}

export const AddressModal: FC<IAddressModalProps> = ({open, onClose, onSave, initialValue}) => {
    const isMounted = useRef(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSelectionInProgress, setIsSelectionInProgress] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [options, setOptions] = useState<ILocation[]>(defaultWhereOptions);
    const [selectedOption, setSelectedOption] = useState<ILocation | null>(initialValue ?? null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Reset state when modal opens or initial value changes
    useEffect(() => {
        if (open) {
            setSelectedOption(initialValue ?? null);
            setSearchQuery("");
            setOptions(defaultWhereOptions);
        }
    }, [open, initialValue]);

    const debouncedSearch = useMemo(() =>
        debounce((value: string) => {
            if (!isMounted.current) return;

            if (!value) {
                setOptions(defaultWhereOptions);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            getSearchLocation(value)
                .then((data) => {
                    if (isMounted.current) {
                        setOptions(data.length > 0 ? data : []);
                    }
                })
                .catch(error => {
                    if (isMounted.current) {
                        console.error('Error fetching location options:', error);
                        setOptions([]);
                    }
                })
                .finally(() => {
                    if (isMounted.current) {
                        setIsLoading(false);
                    }
                });
        }, 300),
    []);

    useEffect(() => {
        if (open) {
            debouncedSearch(searchQuery);
        }

        return () => {
            debouncedSearch.cancel();
            setIsLoading(false);
        };
    }, [searchQuery, open, debouncedSearch]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleSelectOption = useCallback((option: ILocation) => {
        if (isSelectionInProgress) return;

        setIsSelectionInProgress(true);

        let finalOptionState: ILocation | null = null;
        if (option.label_client === "Alle Regionen und Postleitzahlen") {
            finalOptionState = null;
        } else if (option.label_client === "Homeoffice") {
            finalOptionState = {
                ...option,
                radius: 0,
                region: 2,
            };
        } else {
            finalOptionState = option;
        }

        setSelectedOption(finalOptionState);
        requestAnimationFrame(() => {
            setIsSelectionInProgress(false);
        });
    }, [isSelectionInProgress]);

    const handleSave = useCallback(() => {
        onSave(selectedOption);
    }, [onSave, selectedOption]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const memoizedOptions = useMemo(() => {
        return options.map((option) => (
            <WhatOption
                key={`${option.id}-${option.label_client}`}
                onClick={() => handleSelectOption(option)}
                isSelectedOpt={selectedOption?.id === option.id}
            >
                {option.label_client}
            </WhatOption>
        ));
    }, [options, selectedOption, handleSelectOption]);

    return (
        <ModalComponent
            title="Wo suchen Sie?"
            open={open}
            onClose={handleClose}
            onSave={handleSave}
            footerValue={
                <div style={{textAlign: 'left', fontStyle: selectedOption ? 'normal' : 'italic'}}>
                    {selectedOption?.label_client || "Kein Filter (Alle Standorte)"}
                </div>
            }
        >
            <WhereWrapper>
                <Input
                    size="large"
                    style={{marginBottom: 16}}
                    placeholder="Suche um Postleitzahl (empfohlen) oder in Region"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    disabled={isSelectionInProgress}
                />
                {isLoading && <Spin style={{display: 'block', margin: '20px auto'}}/>}
                {!isLoading && memoizedOptions.length === 0 && searchQuery && (
                    <NoResults>Keine Ergebnisse für "{searchQuery}"</NoResults>
                )}
                {!isLoading && memoizedOptions.length > 0 && (
                    <OptionsContainer>{memoizedOptions}</OptionsContainer>
                )}
            </WhereWrapper>
        </ModalComponent>
    );
};

const WhereWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 70vh;
`;

const OptionsContainer = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    margin-top: 8px;

    @media (max-width: 767px) {
        max-height: 40vh;
    }
`;

const NoResults = styled.div`
    padding: 20px;
    text-align: center;
    color: ${colors.text};
`;

